class MTBannerSwitch {
  constructor(element) {
    this.element = element;
    this.slider = element.querySelector('.mt-banner-slider');
    this.slides = Array.from(element.querySelectorAll('.mt-banner-slide'));
    this.dots = Array.from(element.querySelectorAll('.mt-banner-dot'));
    // Get all prev/next buttons (not just the first one)
    this.prevBtns = Array.from(element.querySelectorAll('.mt-banner-nav-prev'));
    this.nextBtns = Array.from(element.querySelectorAll('.mt-banner-nav-next'));
    
    this.currentIndex = 0;
    this.autoplaySpeed = parseInt(element.dataset.autoplaySpeed) || 5000;
    this.autoplayTimer = null;
    this.isTransitioning = false;
    
    this.init();
  }

  init() {
    if (this.slides.length === 0) return;

    // Set first slide as active
    this.slides[0].classList.add('active');

    // Setup event listeners
    this.setupEventListeners();

    // Start autoplay
    this.startAutoplay();

    // Apply responsive navigation positions
    this.applyNavigationPosition();
    window.addEventListener('resize', () => this.applyNavigationPosition());
  }

  setupEventListeners() {
    // Navigation buttons - handle all prev/next buttons
    this.prevBtns.forEach(btn => {
      btn.addEventListener('click', () => this.prevSlide());
    });

    this.nextBtns.forEach(btn => {
      btn.addEventListener('click', () => this.nextSlide());
    });

    // Dots navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Pause on hover
    this.element.addEventListener('mouseenter', () => this.stopAutoplay());
    this.element.addEventListener('mouseleave', () => this.startAutoplay());

    // Touch/swipe support
    this.setupTouchEvents();

    // Keyboard navigation
    this.element.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      }
    });
  }

  setupTouchEvents() {
    let touchStartX = 0;
    let touchEndX = 0;
    let touchStartY = 0;
    let touchEndY = 0;

    this.slider.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    }, { passive: true });

    this.slider.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      touchEndY = e.changedTouches[0].screenY;
      this.handleSwipe(touchStartX, touchEndX, touchStartY, touchEndY);
    }, { passive: true });
  }

  handleSwipe(startX, endX, startY, endY) {
    const diffX = startX - endX;
    const diffY = startY - endY;
    const threshold = 50;

    // Only trigger swipe if horizontal movement is greater than vertical
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        // Swiped left
        this.nextSlide();
      } else {
        // Swiped right
        this.prevSlide();
      }
    }
  }

  goToSlide(index) {
    if (this.isTransitioning || index === this.currentIndex) return;
    if (index < 0 || index >= this.slides.length) return;

    this.isTransitioning = true;

    // Remove active class from current slide and dot
    this.slides[this.currentIndex].classList.remove('active');
    if (this.dots[this.currentIndex]) {
      this.dots[this.currentIndex].classList.remove('active');
    }

    // Update current index
    this.currentIndex = index;

    // Add active class to new slide and dot
    this.slides[this.currentIndex].classList.add('active');
    if (this.dots[this.currentIndex]) {
      this.dots[this.currentIndex].classList.add('active');
    }

    // Reset transition lock after animation completes
    setTimeout(() => {
      this.isTransitioning = false;
    }, 600);

    // Restart autoplay
    this.restartAutoplay();
  }

  nextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }

  startAutoplay() {
    if (this.slides.length <= 1) return;
    
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  applyNavigationPosition() {
    const isMobile = window.innerWidth < 768;
    const position = isMobile 
      ? this.element.dataset.navPositionMobile 
      : this.element.dataset.navPositionDesktop;

    // Remove all position classes
    this.element.classList.remove(
      'nav-image-sides',
      'nav-dot-sides',
      'nav-below-image',
      'nav-hidden'
    );

    // Add current position class
    if (position) {
      this.element.classList.add(`nav-${position}`);
    }
  }

  destroy() {
    this.stopAutoplay();
    window.removeEventListener('resize', () => this.applyNavigationPosition());
  }
}

// Initialize all banner switches on the page
function initMTBannerSwitches() {
  const bannerSwitches = document.querySelectorAll('.mt-banner-switch');
  
  bannerSwitches.forEach(element => {
    if (!element.mtBannerSwitchInstance) {
      element.mtBannerSwitchInstance = new MTBannerSwitch(element);
    }
  });
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMTBannerSwitches);
} else {
  initMTBannerSwitches();
}

// Re-initialize when Shopify section is loaded (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const element = event.target.querySelector('.mt-banner-switch');
    if (element && !element.mtBannerSwitchInstance) {
      element.mtBannerSwitchInstance = new MTBannerSwitch(element);
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    const element = event.target.querySelector('.mt-banner-switch');
    if (element && element.mtBannerSwitchInstance) {
      element.mtBannerSwitchInstance.destroy();
      element.mtBannerSwitchInstance = null;
    }
  });

  document.addEventListener('shopify:section:reorder', initMTBannerSwitches);
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MTBannerSwitch;
}
